import { useState } from "react";
import { useTranslation } from "react-i18next";

export function BlogPage() {
  return (
    <main>
      <Main />
      <Content />
    </main>
  );
}

function Main() {
  const { t } = useTranslation();
  
  return (
    <div
      className="py-16 relative"
      style={{
        backgroundImage: "url(/assets/img/blog/main.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="container px-8 md:px-[100px] py-[130px] relative z-10">
        <h1 className="text-left md:leading-[64px] text-[40px] md:text-5xl font-light text-white mb-8">
          {t('BlogPage.main.title')}
        </h1>
      </div>
    </div>
  );
}

function Content() {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBlog, setSelectedBlog] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 6;

  // Blog images mapping
  const blogImages = [
    ["/assets/img/blog/6Junio2024.png"],
    ["/assets/img/blog/6Junio2024-2.png"],
    [
      "/assets/img/blog/13Junio2024.1.png",
      "/assets/img/blog/13Junio2024.png",
    ],
    ["/assets/img/blog/16Junio2024.png"],
    [
      "/assets/img/blog/20JUNIO2024.png",
      "/assets/img/blog/20JUNIO2024(2).png",
      "/assets/img/blog/20JUNIO2024(3).png",
      "/assets/img/blog/20JUNIO2024(AVISO).png",
    ],
    ["/assets/img/blog/28JUNIO2024.png"],
    ["/assets/img/blog/17JULIO2024.png"],
    ["/assets/img/blog/24Julio2024.png"],
    [
      "/assets/img/blog/1August2024.png",
      "/assets/img/blog/1August2024pt2.png",
    ],
    ["/assets/img/blog/22AGOSTO2024.png"],
    [
      "/assets/img/blog/31AGOSTO2024P1.png",
      "/assets/img/blog/31AGOSTO2024P2.png",
    ],
    ["/assets/img/blog/7SEPTIEMBRE2024.png"],
    ["/assets/img/blog/20SEPT2024.png"],
    ["/assets/img/blog/19OCT2024.png"],
    ["/assets/img/blog/31OCT2025.png"],
  ];

  const blogsData = t('BlogPage.content.blogs', { returnObjects: true }) as any[];
  const blogs = (blogsData || []).map((blog, index) => ({
    ...blog,
    img: blogImages[index]
  }));

  const parseDate = (dateStr: string): number => {
    // Parse date in format YYYY/MM/DD
    const parts = dateStr.split("/");
    const año = parseInt(parts[0]);
    const mes = parseInt(parts[1]);
    const dia = parseInt(parts[2]);

    return new Date(año, mes - 1, dia).getTime();
  };

  const sortedBlogs = [...blogs].sort(
    (a, b) => parseDate(b.dateFormat) - parseDate(a.dateFormat)
  );

  const totalPages = Math.ceil(sortedBlogs.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBlogs = sortedBlogs.slice(startIndex, endIndex);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      document.getElementById("blog")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      document.getElementById("blog")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const openModal = (blog: any) => {
    setSelectedBlog(blog);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBlog(null);
  };

  return (
    <div id="blog" className="bg-[#F2F6FB] relative pb-24 md:pb-32 w-full">
      <img
        className="absolute top-0 left-0 z-0"
        src="/assets/img/diagonal-left.png"
        alt="Diagonal"
        loading="lazy"
        decoding="async"
      />
      <img
        className="absolute bottom-0 right-0 z-0"
        src="/assets/img/diagonal-right-down.png"
        alt="Diagonal"
        loading="lazy"
        decoding="async"
      />
      <div className="mx-auto px-8 md:px-28 pt-16 relative z-10">
        <div
          className={`space-y-16  transition-all duration-1000 delay-600 z-10`}
        >
          <div className="md:flex space-y-12 md:space-y-0 justify-between">
            <div className="w-full flex flex-col space-y-8 text-left">
              <h2 className="z-10 md:text-6xl text-4xl font-light text-left md:leading-[80px] leading-[50px]">
                {t('BlogPage.content.title')}
              </h2>
            </div>
            <div className="w-full text-left relative">
              <div className="md:text-2xl text-sm font-medium mb-14 text-left">
                <p className={`text-[#5B5D60]`}>
                  {t('BlogPage.content.subtitle')}
                </p>
              </div>
            </div>
          </div>
          <div className="md:grid grid-cols-2 md:gap-x-32 gap-y-16 gap-x-4">
            {currentBlogs.map((blog, index) => (
              <div
                className="md:mt-4 mt-8 space-y-4 group"
                key={`${blog.title}-${blog.date}-${index}`}
              >
                <div className="relative rounded-xl space-y-4 text-left">
                  <img
                    src={Array.isArray(blog.img) ? blog.img[0] : blog.img}
                    alt={blog.title}
                    className="w-full h-[283px] object-cover rounded-xl"
                    loading="lazy"
                    decoding="async"
                    fetchPriority="high"
                  />
                  <p className="font-medium md:text-2xl text-xl text-[#111928]">
                    {blog.title}
                  </p>
                  <p className="font-normal md:text-lg text-base text-[#637381] whitespace-pre-line line-clamp-2">
                    {blog.description}
                  </p>
                  <button
                    onClick={() => openModal(blog)}
                    className="bg-transparent transition-all duration-300 text-[#0B4494] hover:text-[#0271BA] border border-[#0B4494] hover:border-[#0271BA] text-sm md:text-base py-[8px] px-[40px] rounded-3xl"
                  >
                    {t('BlogPage.content.readMore')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center space-x-12 mt-8 relative z-10">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`transition-all duration-300 text-white text-sm md:text-base h-[70px] w-[70px] rounded-full ${
                currentPage === 1
                  ? "bg-[#E9E9E9] cursor-not-allowed opacity-50"
                  : "bg-[#E9E9E9] hover:bg-[#E9E9E9]/80 cursor-pointer"
              }`}
            >
              <img
                src="/assets/svg/arrow-left.svg"
                alt="Arrow Left"
                className="m-auto w-6 h-6 md:w-8 md:h-8"
                loading="lazy"
                decoding="async"
              />
            </button>
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`transition-all duration-300 text-white text-sm md:text-base h-[70px] w-[70px] rounded-full ${
                currentPage === totalPages
                  ? "bg-[#192457] cursor-not-allowed opacity-50"
                  : "bg-[#192457] hover:bg-[#0271BA] cursor-pointer"
              }`}
            >
              <img
                src="/assets/svg/arrow-right-white.svg"
                alt="Arrow Right"
                className="m-auto w-6 h-6 md:w-8 md:h-8"
                loading="lazy"
                decoding="async"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedBlog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] px-8 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white p-6 flex justify-end items-center">
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 transition-colors text-3xl"
              >
                <img src="/assets/svg/x.svg" alt="Close" className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-8 md:mx-24">
              <p className="text-sm md:text-xl text-[#022D6C] font-semibold">
                {t('BlogPage.content.modal.label')}
              </p>
              <h3 className="text-2xl md:text-5xl font-semibold text-[#111928]">
                {selectedBlog.title}
              </h3>
              <p className="text-sm md:text-xl text-[#757575] font-normal">
                {selectedBlog.date}
              </p>
            </div>

            <div className="space-y-8 text-left md:m-8 my-8 ">
              <p className="text-sm md:text-base text-[#151515] whitespace-pre-line">
                {selectedBlog.description}
              </p>

              <p className="text-sm md:text-base text-[#151515] break-words">
                {selectedBlog.hashtags}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {selectedBlog.img.map((img: string, index: number) => {
                  const isLast = index === selectedBlog.img.length - 1;
                  const isOddCount = selectedBlog.img.length % 2 !== 0;

                  return (
                    <div
                      key={img + index}
                      className={isLast && isOddCount ? "col-span-2" : ""}
                    >
                      <img
                        src={img}
                        alt={selectedBlog.title + index}
                        className="w-full h-auto rounded-xl"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
